import { useEffect, useState } from "react";

import * as userFeedbackServices from "../../services/userFeedbackService";
import { CustomNotification } from "../../util/CustomNotification";
import useValue from "../../hooks/useValue";
import useTablePagination from "../../hooks/useTablePagination";
import { DefaultPagination } from "../../constants/generalConstants";

import type { FeedbackRecordItem } from "../../shared/interface/UserFeedback.interface";

export const useUserFeedback = () => {
  /* STATE */
  const [searchedText, setSearchedText] = useState<string>("");
  const [feedbackData, setFeedbackData] = useState<FeedbackRecordItem[]>([]);
  const [reFetch, setRefetch] = useState(true);
  const [selectedCustomerId, setSelectedCustomerId] = useState<number | null>(null);
  const [selectedLocationId, setSelectedLocationId] = useState<number | null>(null);

  /* CUSTOM HOOKS */
  const { value: loading, handleDisableValue: handleDisableLoading, handleEnableValue: handleEnableLoading } = useValue(false);
  const { pagination, handleChangePagination, handleChangeTotalSize, handleResetPagination } = useTablePagination(0);

  /* HANDLERS */
  const handleChangeOnSearch = (value: string) => {
    handleResetPagination();
    const trimmedValue = value?.replace(/^\s+/, "");
    setSearchedText(trimmedValue);
    setRefetch(true);
  };

  const handleChangeSearch = (value: string) => {
    handleResetPagination();
    const trimmedValue = value?.replace(/^\s+/, "");
    setSearchedText(trimmedValue);
    if (!trimmedValue) {
      setRefetch(true);
    }
  };

  const handleChangeCustomer = (customerId: number | null) => {
    setSelectedLocationId(null);
    setSelectedCustomerId(customerId);
  };
  const handleChangeLocation = (locationId: number | null) => {
    setSelectedLocationId(locationId);
  };

  /* EFFECTS */

  useEffect(() => {
    const fetchFeedbackData = async () => {
      try {
        handleEnableLoading();

        const paramsObject = {
          searchQuery: searchedText,
          page: pagination?.current || DefaultPagination.PAGE,
          pageSize: pagination?.pageSize || DefaultPagination.PAGE_SIZE,
          customerId: selectedCustomerId,
          locationId: selectedLocationId
        };

        const response = await userFeedbackServices.getAllFeedback(paramsObject);

        const formattedResponse = response?.data?.rows?.map((item, index) => ({
          ...item,
          sr_no: index + 1,
          user: `${item?.full_name ?? ""} <br/> ${item?.mobile_number ?? ""}`
        }));

        handleChangeTotalSize(response?.data?.count || 0);
        setFeedbackData(formattedResponse);
      } catch (error) {
        CustomNotification.showErrorMessage(error);
      } finally {
        setRefetch(false);
        handleDisableLoading();
      }
    };

    if (reFetch) fetchFeedbackData();
  }, [reFetch]);

  useEffect(() => {
    if (pagination?.current && pagination?.pageSize) {
      setRefetch(true);
    } else {
      setRefetch(false);
    }
  }, [pagination?.current, pagination?.pageSize, selectedCustomerId, selectedLocationId]);

  return {
    loading,
    searchedText,
    feedbackData,
    pagination,
    handleChangeOnSearch,
    handleChangeSearch,
    handleChangePagination,
    selectedCustomerId,
    setSelectedCustomerId,
    selectedLocationId,
    setSelectedLocationId,
    handleChangeCustomer,
    handleChangeLocation
  };
};
